import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button, Card } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { useStudioPageChromeOverride } from "../../common/StudioPageChromeContext";
import HKPProductInventoryPicker from "../../components/products/HKPProductInventoryPicker";
import { fetchPickerProducts, PickerProduct, ProductPageError, ProductPageLoader } from "../../components/products/productSelection";
import { applyCareTemplate, careApplyStatus, getCareTemplate } from "../../care/careApi";
import type { CareTemplate } from "../../care/careTypes";
import { filledSectionCount } from "../../care/careValues";
import { canUseCare } from "../../helpers/careEnabled";
import { readAuthUser } from "../../helpers/studioModules";

const REASONS: Record<string, string> = {
  CURRENT: "Already uses this care.",
  NOT_FOUND: "Not a product of this studio.",
};

/** Pick products and give them all this care template. Products on another template switch to this one. */
const CareApplyToProducts = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [template, setTemplate] = useState<CareTemplate | null>(null);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmBack, setConfirmBack] = useState(false);
  const selected = useRef<PickerProduct[]>([]);
  const busy = useRef(false);
  const back = useCallback(() => history.push("/care-templates"), [history]);

  useEffect(() => {
    let current = true;
    getCareTemplate(id).then((res) => {
      if (!current) return;
      if (res.success && res.data) setTemplate(res.data);
      else setError(res.message || "This care template could not be loaded.");
    });
    return () => {
      current = false;
    };
  }, [id]);

  const requestBack = useCallback(() => {
    if (busy.current) return;
    if (selected.current.length) setConfirmBack(true);
    else back();
  }, [back]);

  useStudioPageChromeOverride({ title: "Apply care to products", subtitle: template?.name, onBack: requestBack });

  const loadPage = useCallback<ProductPageLoader>(
    async (query, page, limit) => {
      const result = await fetchPickerProducts(query, page, limit);
      const ids = Array.from(new Set(result.products.map((row) => row.productUniqueId).filter((pid) => typeof pid === "string" && pid.trim())));
      if (!ids.length) return { products: [], total: result.total };
      const status = await careApplyStatus(id, ids);
      if (!status.success || !status.data) throw new ProductPageError(status.message || "Unable to check care for these products.");
      return {
        ...result,
        products: result.products.map((product) => {
          const state = status.data?.[product.productUniqueId]?.state;
          return { ...product, canAdd: state !== "CURRENT" && state !== "NOT_FOUND", reason: state ? REASONS[state] : undefined };
        }),
      };
    },
    [id]
  );

  const apply = async (products: PickerProduct[]) => {
    if (!template) return { addedIds: [] };
    busy.current = true;
    setFeedback("");
    try {
      const ids = products.map((p) => p.productUniqueId);
      const res = await applyCareTemplate(template.id, ids, template.version);
      if (!res.success || !res.data) {
        setFeedback(
          res.code === "TEMPLATE_CHANGED"
            ? "This template was edited since you opened this page. Go back and open it again."
            : res.message || "Care could not be applied. Please try again."
        );
        if (!res.data) throw new Error("Unconfirmed apply");
        return { addedIds: [] };
      }
      const done = res.data.results.filter((r) => r.result === "APPLIED" || r.result === "UNCHANGED").map((r) => r.productUniqueId);
      const failed = res.data.results.filter((r) => r.result !== "APPLIED" && r.result !== "UNCHANGED").map((r) => r.productUniqueId);
      const cleared = res.data.results.filter((r) => r.overridesCleared).length;
      notifications({
        title: "Care applied",
        message:
          `${res.data.applied} product${res.data.applied === 1 ? "" : "s"} now use “${template.name}”.` +
          (cleared ? ` ${cleared} had their own changes from another template; those were replaced.` : "") +
          (failed.length ? ` ${failed.length} could not be updated (not this studio's, changed elsewhere, or the product's own changes would leave no care).` : ""),
        type: failed.length ? "warning" : "success",
      });
      return { addedIds: done, rejectedIds: failed };
    } finally {
      busy.current = false;
    }
  };

  if (!canUseCare(readAuthUser()?.users?.role)) {
    return (
      <div className="userMainDiv">
        <Card className="createProductCard p-3">Care & Preservation is not switched on for your studio.</Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="userMainDiv">
        <Card className="createProductCard p-3">
          <p role="alert">{error}</p>
          <Button className="btn-hastkala-outline" onClick={back}>
            Back to care templates
          </Button>
        </Card>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="userMainDiv">
        <p role="status">Loading…</p>
      </div>
    );
  }

  return (
    <>
      <p className="catalogue-add-from-inventory-lead">
        Give the selected products the care from <strong>{template.name}</strong> ({filledSectionCount(template.sections)} of 6 sections). Products
        that use another template switch to this one.
      </p>
      <HKPProductInventoryPicker
        loadPage={loadPage}
        onAdd={apply}
        onDone={requestBack}
        doneLabel="Cancel"
        addedReason="Care applied."
        actionLabel={(count) => (count > 1 ? `Apply care to ${count} products` : "Apply care")}
        busyLabel="Applying…"
        onSelectionChange={(products) => {
          selected.current = products;
        }}
        feedback={feedback ? <p role="alert">{feedback}</p> : undefined}
      />
      <HKPConfirmDialog
        open={confirmBack}
        title="Leave without applying?"
        description="The products you selected have not been given this care yet."
        confirmLabel="Leave"
        onCancel={() => setConfirmBack(false)}
        onConfirm={back}
      />
    </>
  );
};

export default CareApplyToProducts;
